import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isGeneralAdmin } from '../utils/adminMenu';
import adminMonitorService from '../services/AdminMonitorService';
import './AdminMonitor.css';

const formatNumber = (value) => Number(value || 0).toLocaleString('es-AR');

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat('es-AR', {
      dateStyle: 'short',
      timeStyle: 'medium'
    }).format(new Date(value));
  } catch (error) {
    return '-';
  }
};

const formatPercent = (value) => `${Math.round(Number(value || 0))}%`;

const pick = (item, camel, pascal, fallback = null) => item?.[camel] ?? item?.[pascal] ?? fallback;

const queueHealthLabel = (health) => {
  switch (health) {
    case 'critical':
      return 'Crítica';
    case 'warning':
      return 'Atención';
    case 'busy':
      return 'Ocupada';
    default:
      return 'Normal';
  }
};

const AdminMonitor = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const canViewMonitor = isGeneralAdmin(user);

  const loadSnapshot = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');

    try {
      const response = await adminMonitorService.getSnapshot();
      setSnapshot(response);
    } catch (err) {
      setError(err?.message || 'No se pudo cargar el monitor.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!canViewMonitor) return;
    loadSnapshot(false);
  }, [canViewMonitor, loadSnapshot]);

  useEffect(() => {
    if (!canViewMonitor) return undefined;
    const intervalSeconds = Number(pick(snapshot, 'refreshIntervalSeconds', 'RefreshIntervalSeconds', 180)) || 180;
    const interval = window.setInterval(() => loadSnapshot(true), intervalSeconds * 1000);
    return () => window.clearInterval(interval);
  }, [canViewMonitor, loadSnapshot, snapshot]);

  const rabbitMq = pick(snapshot, 'rabbitMq', 'RabbitMq', {});
  const system = pick(snapshot, 'system', 'System', {});
  const gpu = pick(snapshot, 'gpu', 'Gpu', {});
  const ollama = pick(snapshot, 'ollama', 'Ollama', {});
  const queues = pick(rabbitMq, 'queues', 'Queues', []);
  const listeners = pick(snapshot, 'listeners', 'Listeners', []);
  const warnings = pick(snapshot, 'warnings', 'Warnings', []);
  const gpuDevices = pick(gpu, 'devices', 'Devices', []) || [];
  const modelServerProvider = pick(ollama, 'provider', 'Provider', 'Ollama') || 'Ollama';
  const gpuMemoryTotalBytes = gpuDevices.reduce((total, device) => total + Number(pick(device, 'memoryTotalBytes', 'MemoryTotalBytes', 0)), 0);
  const gpuMemoryUsedBytes = gpuDevices.reduce((total, device) => total + Number(pick(device, 'memoryUsedBytes', 'MemoryUsedBytes', 0)), 0);
  const gpuMemoryUsedPercent = gpuMemoryTotalBytes > 0 ? Math.round((gpuMemoryUsedBytes * 100) / gpuMemoryTotalBytes) : 0;

  const maxQueueMessages = useMemo(() => {
    const maxCurrent = queues.reduce((max, queue) => Math.max(max, pick(queue, 'messages', 'Messages', 0)), 0);
    const maxPeak = queues.reduce((max, queue) => Math.max(max, pick(queue, 'peakMessagesObserved', 'PeakMessagesObserved', 0)), 0);
    return Math.max(maxCurrent, maxPeak, 1);
  }, [queues]);

  if (!canViewMonitor) {
    return (
      <main className="admin-monitor-page">
        <section className="admin-monitor-denied">
          <i className="fas fa-lock" />
          <h1>Acceso restringido</h1>
          <p>El monitor operativo está disponible sólo para usuarios Admin.</p>
          <button type="button" onClick={() => navigate('/dashboard')}>
            Volver
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-monitor-page">
      <header className="monitor-header">
        <div>
          <p className="monitor-eyebrow">Operación AVE</p>
          <h1>Monitor</h1>
          <p className="monitor-subtitle">
            Colas RabbitMQ, listeners AVE, recursos del servidor y modelos cargados en el servidor de modelos.
          </p>
        </div>
        <div className="monitor-actions">
          <button type="button" className="monitor-secondary" onClick={() => navigate('/admin/select-regional')}>
            <i className="fas fa-arrow-left" />
            Regionales
          </button>
          <button type="button" className="monitor-primary" onClick={() => loadSnapshot(true)} disabled={refreshing}>
            <i className={`fas ${refreshing ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            Actualizar
          </button>
        </div>
      </header>

      {loading && (
        <div className="monitor-state">
          <i className="fas fa-spinner fa-spin" />
          Cargando monitor...
        </div>
      )}

      {!loading && error && (
        <div className="monitor-state error">
          <i className="fas fa-triangle-exclamation" />
          {error}
        </div>
      )}

      {!loading && !error && snapshot && (
        <>
          <section className="monitor-status-strip">
            <div>
              <span>Última lectura</span>
              <strong>{formatDateTime(pick(snapshot, 'timestamp', 'Timestamp'))}</strong>
            </div>
            <div>
              <span>Auto refresh</span>
              <strong>{formatNumber(pick(snapshot, 'refreshIntervalSeconds', 'RefreshIntervalSeconds', 180))}s</strong>
            </div>
            <div>
              <span>RabbitMQ</span>
              <strong className={pick(rabbitMq, 'available', 'Available') ? 'text-ok' : 'text-critical'}>
                {pick(rabbitMq, 'available', 'Available') ? 'Online' : 'Error'}
              </strong>
            </div>
            <div>
              <span>{modelServerProvider}</span>
              <strong className={pick(ollama, 'available', 'Available') ? 'text-ok' : 'text-critical'}>
                {pick(ollama, 'available', 'Available') ? 'Online' : 'Error'}
              </strong>
            </div>
          </section>

          {warnings.length > 0 && (
            <section className="monitor-warnings">
              {warnings.map((warning, index) => (
                <div key={`${warning}-${index}`} className="monitor-warning">
                  <i className="fas fa-circle-info" />
                  <span>{warning}</span>
                </div>
              ))}
            </section>
          )}

          <section className="monitor-summary-grid">
            <article className="monitor-metric">
              <span>Mensajes en colas</span>
              <strong>{formatNumber(pick(rabbitMq, 'totalMessages', 'TotalMessages', 0))}</strong>
              <small>Listos {formatNumber(pick(rabbitMq, 'totalReady', 'TotalReady', 0))} · En proceso {formatNumber(pick(rabbitMq, 'totalUnacknowledged', 'TotalUnacknowledged', 0))}</small>
            </article>
            <article className="monitor-metric">
              <span>Listeners activos</span>
              <strong>{formatNumber(listeners.filter((item) => pick(item, 'status', 'Status') === 'Activo').length)}</strong>
              <small>{formatNumber(pick(rabbitMq, 'totalConsumers', 'TotalConsumers', 0))} consumers RabbitMQ</small>
            </article>
            <article className="monitor-metric">
              <span>Pico histórico observado</span>
              <strong>{formatNumber(pick(rabbitMq, 'historicalPeakMessagesObserved', 'HistoricalPeakMessagesObserved', 0))}</strong>
              <small>{formatDateTime(pick(rabbitMq, 'historicalPeakObservedAt', 'HistoricalPeakObservedAt'))}</small>
            </article>
            <article className="monitor-metric">
              <span>CPU</span>
              <strong>{formatPercent(pick(system, 'cpuUsagePercent', 'CpuUsagePercent', 0))}</strong>
              <small>Load {pick(system, 'loadAverage1m', 'LoadAverage1m', 0)} / {pick(system, 'loadAverage5m', 'LoadAverage5m', 0)} / {pick(system, 'loadAverage15m', 'LoadAverage15m', 0)}</small>
            </article>
            <article className="monitor-metric">
              <span>RAM</span>
              <strong>{formatPercent(pick(system, 'memoryUsedPercent', 'MemoryUsedPercent', 0))}</strong>
              <small>{formatBytes(pick(system, 'memoryUsedBytes', 'MemoryUsedBytes', 0))} de {formatBytes(pick(system, 'memoryTotalBytes', 'MemoryTotalBytes', 0))}</small>
            </article>
            <article className="monitor-metric">
              <span>{gpuDevices.length > 0 ? 'VRAM GPU' : `VRAM ${modelServerProvider}`}</span>
              <strong>{gpuDevices.length > 0 ? formatPercent(gpuMemoryUsedPercent) : formatBytes(pick(ollama, 'totalLoadedVramBytes', 'TotalLoadedVramBytes', 0))}</strong>
              <small>
                {gpuDevices.length > 0
                  ? `${formatBytes(gpuMemoryUsedBytes)} de ${formatBytes(gpuMemoryTotalBytes)}`
                  : `${formatNumber(pick(ollama, 'totalLoadedModels', 'TotalLoadedModels', 0))} modelos cargados`}
              </small>
            </article>
          </section>

          <section className="monitor-section">
            <div className="monitor-section-header">
              <div>
                <p className="monitor-eyebrow">RabbitMQ</p>
                <div className="monitor-title-with-info">
                  <h2>Colas AVE por regional</h2>
                  <button
                    type="button"
                    className="monitor-info-btn"
                    aria-label="Información sobre las colas AVE por regional"
                  >
                    <i className="fas fa-info" />
                    <span className="monitor-info-popover" role="tooltip">
                      En estas colas se guardan los trabajos AVE pendientes o en proceso por regional: creación de asistentes,
                      archivos subidos, parseo del material, carga hacia RagFlow y actualización del estado del proceso.
                    </span>
                  </button>
                </div>
              </div>
              <span>{formatNumber(pick(rabbitMq, 'totalQueues', 'TotalQueues', queues.length))} colas</span>
            </div>
            <div className="queue-grid">
              {queues.map((queue) => {
                const name = pick(queue, 'name', 'Name', '');
                const messages = pick(queue, 'messages', 'Messages', 0);
                const health = pick(queue, 'health', 'Health', 'ok');
                const width = Math.max(3, Math.round((messages / maxQueueMessages) * 100));
                return (
                  <article key={name} className={`queue-card ${health}`}>
                    <div className="queue-card-head">
                      <div>
                        <span>{pick(queue, 'regionalSigla', 'RegionalSigla') || 'Regional'}</span>
                        <h3>{pick(queue, 'regionalName', 'RegionalName', name)}</h3>
                      </div>
                      <strong>{queueHealthLabel(health)}</strong>
                    </div>
                    <div className="queue-load">
                      <div style={{ width: `${width}%` }} />
                    </div>
                    <div className="queue-stats">
                      <span><strong>{formatNumber(messages)}</strong> total</span>
                      <span><strong>{formatNumber(pick(queue, 'messagesReady', 'MessagesReady', 0))}</strong> listos</span>
                      <span><strong>{formatNumber(pick(queue, 'messagesUnacknowledged', 'MessagesUnacknowledged', 0))}</strong> en proceso</span>
                      <span><strong>{formatNumber(pick(queue, 'consumers', 'Consumers', 0))}</strong> consumers</span>
                    </div>
                    <div className="queue-meta">
                      <span>Pico {formatNumber(pick(queue, 'peakMessagesObserved', 'PeakMessagesObserved', 0))}</span>
                      <span>Pub/s {pick(queue, 'publishRate', 'PublishRate', 0)}</span>
                      <span>Ack/s {pick(queue, 'ackRate', 'AckRate', 0)}</span>
                    </div>
                    <code title={name}>{name}</code>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="monitor-section">
            <div className="monitor-section-header">
              <div>
                <p className="monitor-eyebrow">Backend</p>
                <h2>Listeners AVE</h2>
              </div>
            </div>
            <div className="monitor-table-wrapper">
              <table className="monitor-table">
                <thead>
                  <tr>
                    <th>Regional</th>
                    <th>Estado</th>
                    <th>Consumers</th>
                    <th>Activo</th>
                    <th>Procesados</th>
                    <th>Fallidos</th>
                    <th>Último mensaje</th>
                  </tr>
                </thead>
                <tbody>
                  {listeners.map((listener) => {
                    const status = pick(listener, 'status', 'Status', '');
                    return (
                      <tr key={pick(listener, 'queueName', 'QueueName')}>
                        <td>
                          <strong>{pick(listener, 'regionalName', 'RegionalName')}</strong>
                          <small>{pick(listener, 'regionalSigla', 'RegionalSigla') || pick(listener, 'queueName', 'QueueName')}</small>
                        </td>
                        <td><span className={`listener-pill ${status === 'Activo' ? 'ok' : 'critical'}`}>{status}</span></td>
                        <td>{formatNumber(pick(listener, 'rabbitMqConsumers', 'RabbitMqConsumers', 0))}</td>
                        <td>{formatNumber(pick(listener, 'activeMessages', 'ActiveMessages', 0))}</td>
                        <td>{formatNumber(pick(listener, 'processedCount', 'ProcessedCount', 0))}</td>
                        <td>{formatNumber(pick(listener, 'failedCount', 'FailedCount', 0))}</td>
                        <td>{formatDateTime(pick(listener, 'lastMessageStartedAt', 'LastMessageStartedAt'))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="monitor-two-columns">
            <article className="monitor-section">
              <div className="monitor-section-header">
                <div>
                  <p className="monitor-eyebrow">Servidor</p>
                  <h2>CPU, RAM y disco</h2>
                </div>
              </div>
              <div className="resource-bars">
                <div className="resource-row">
                  <span>CPU</span>
                  <div><i style={{ width: `${Math.min(100, pick(system, 'cpuUsagePercent', 'CpuUsagePercent', 0))}%` }} /></div>
                  <strong>{formatPercent(pick(system, 'cpuUsagePercent', 'CpuUsagePercent', 0))}</strong>
                </div>
                <div className="resource-row">
                  <span>RAM</span>
                  <div><i style={{ width: `${Math.min(100, pick(system, 'memoryUsedPercent', 'MemoryUsedPercent', 0))}%` }} /></div>
                  <strong>{formatPercent(pick(system, 'memoryUsedPercent', 'MemoryUsedPercent', 0))}</strong>
                </div>
              </div>
              <div className="disk-list">
                {(pick(system, 'disks', 'Disks', []) || []).map((disk) => {
                  const usedPercent = Math.max(0, Math.min(100, Number(pick(disk, 'usedPercent', 'UsedPercent', 0)) || 0));
                  return (
                    <div key={pick(disk, 'name', 'Name')} className="disk-item">
                      <span>{pick(disk, 'name', 'Name')}</span>
                      <div className="disk-usage-bar" aria-label={`Uso de disco ${formatPercent(usedPercent)}`}>
                        <i style={{ width: `${usedPercent}%` }} />
                      </div>
                      <strong>{formatPercent(usedPercent)}</strong>
                      <small>{formatBytes(pick(disk, 'usedBytes', 'UsedBytes', 0))} de {formatBytes(pick(disk, 'totalBytes', 'TotalBytes', 0))}</small>
                    </div>
                  );
                })}
              </div>
            </article>

            <article className="monitor-section">
              <div className="monitor-section-header">
                <div>
                  <p className="monitor-eyebrow">{modelServerProvider} / GPU</p>
                  <h2>Modelos cargados</h2>
                </div>
              </div>
              {!pick(gpu, 'available', 'Available') && (
                <div className="gpu-warning">
                  <i className="fas fa-microchip" />
                  <span>{pick(gpu, 'error', 'Error') || 'GPU no expuesta por nvidia-smi en este servidor.'}</span>
                </div>
              )}
              {gpuDevices.length > 0 && (
                <div className="gpu-device-list">
                  {gpuDevices.map((device) => {
                    const totalBytes = pick(device, 'memoryTotalBytes', 'MemoryTotalBytes', 0);
                    const usedBytes = pick(device, 'memoryUsedBytes', 'MemoryUsedBytes', 0);
                    const memoryPercent = pick(device, 'memoryUsedPercent', 'MemoryUsedPercent', 0);
                    return (
                      <div key={`${pick(device, 'index', 'Index', 0)}-${pick(device, 'name', 'Name', 'gpu')}`} className="gpu-device">
                        <div className="gpu-device-head">
                          <div>
                            <strong>{pick(device, 'name', 'Name', 'GPU')}</strong>
                            <span>GPU {pick(device, 'index', 'Index', 0)} · {pick(gpu, 'provider', 'Provider', 'NVIDIA')}</span>
                          </div>
                          <strong>{formatPercent(memoryPercent)}</strong>
                        </div>
                        <div className="gpu-memory-bar">
                          <i style={{ width: `${Math.min(100, Number(memoryPercent || 0))}%` }} />
                        </div>
                        <div className="gpu-device-stats">
                          <span><small>VRAM</small><strong>{formatBytes(usedBytes)} / {formatBytes(totalBytes)}</strong></span>
                          <span><small>Uso GPU</small><strong>{formatPercent(pick(device, 'utilizationPercent', 'UtilizationPercent', 0))}</strong></span>
                          <span><small>Temp.</small><strong>{pick(device, 'temperatureCelsius', 'TemperatureCelsius', 0)}°C</strong></span>
                          <span><small>Potencia</small><strong>{pick(device, 'powerDrawWatts', 'PowerDrawWatts', 0)} W</strong></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="ollama-model-list">
                {(pick(ollama, 'loadedModels', 'LoadedModels', []) || []).map((model) => {
                  const metadata = [
                    pick(model, 'parameterSize', 'ParameterSize'),
                    pick(model, 'quantization', 'Quantization')
                  ].filter(Boolean).join(' · ');
                  const contextLength = Number(pick(model, 'contextLength', 'ContextLength', 0));
                  return (
                    <div key={pick(model, 'name', 'Name')} className="ollama-model">
                      <div>
                        <strong>{pick(model, 'name', 'Name')}</strong>
                        {metadata && <span>{metadata}</span>}
                        {pick(model, 'model', 'Model') && <small>{pick(model, 'model', 'Model')}</small>}
                      </div>
                      <div>
                        <small>VRAM</small>
                        <strong>{formatBytes(pick(model, 'sizeVramBytes', 'SizeVramBytes', 0))}</strong>
                      </div>
                      <div>
                        <small>Contexto</small>
                        <strong>{contextLength > 0 ? formatNumber(contextLength) : '-'}</strong>
                      </div>
                      <div>
                        <small>Expira</small>
                        <strong>{formatDateTime(pick(model, 'expiresAt', 'ExpiresAt'))}</strong>
                      </div>
                    </div>
                  );
                })}
                {pick(ollama, 'loadedModels', 'LoadedModels', []).length === 0 && (
                  <div className="monitor-empty">No hay modelos cargados actualmente en {modelServerProvider}.</div>
                )}
              </div>
            </article>
          </section>
        </>
      )}
    </main>
  );
};

export default AdminMonitor;
